import { IExportedFile, IPaginated, IPaginationSearch, TableExportFormat } from '@shared/common';

import { ContractInspectionIdMiddleSegment, StatusContractInspection } from '../../contracts/types/enums';
import { StatusInspection } from '../../inspections/types/enums';

import {
  RentalInspectionKind,
  RentalRecordsSortField,
  UsageByLocationSortField,
  UsageDateKind,
  UsageLocationGroupBy,
  UsageStage,
  UsageTrendBucket,
} from './enums';
import {
  IAnalyticsMetricAnnotation,
  IUsageCohortBreakdown,
  IUsageDurationStats,
  IUsageExportColumnAnnotation,
  IUsagePeriod,
} from './usage-analytics';

/**
 * Rental companion track of the usage analytics: the counted unit is the **contract**
 * (`rental_contract`), not the inspection. A contract is *requested* when it is created and
 * *completed* when it first enters `COMPLETED` (`rental_contract.service_completed_at`, stamped by
 * the entity and backfilled from the outbox history). Stages map contract statuses through
 * `USAGE_CONTRACT_STAGE_STATUSES`; `MISSED_PICK_UP` is the rental "attention" signal.
 */

/** Child inspection id middle segment → kind. */
export const RENTAL_INSPECTION_KIND_SEGMENTS: Readonly<
  Record<ContractInspectionIdMiddleSegment, RentalInspectionKind>
> = {
  [ContractInspectionIdMiddleSegment.PICK_UP]: RentalInspectionKind.PICK_UP,
  [ContractInspectionIdMiddleSegment.DROP_OFF]: RentalInspectionKind.DROP_OFF,
  [ContractInspectionIdMiddleSegment.EMPLOYEE]: RentalInspectionKind.EMPLOYEE,
};

export const RENTAL_INSPECTION_KIND_LABELS: Readonly<Record<RentalInspectionKind, string>> = {
  [RentalInspectionKind.PICK_UP]: 'Pick-up',
  [RentalInspectionKind.DROP_OFF]: 'Drop-off',
  [RentalInspectionKind.EMPLOYEE]: 'Employee',
};

/** Contract statuses that count as "requiring attention" on the rental track. */
export const RENTAL_ATTENTION_STATUSES: readonly StatusContractInspection[] = [StatusContractInspection.MISSED_PICK_UP];

export const RENTAL_STAGE_LABELS: Readonly<Record<UsageStage, string>> = {
  [UsageStage.AWAITING]: 'Awaiting pick-up',
  [UsageStage.CAPTURE]: 'In rental',
  [UsageStage.REVIEW]: 'Estimate requested',
  [UsageStage.COMPLETED]: 'Completed',
  [UsageStage.CLOSED]: 'Cancelled',
};

/**
 * Shared filter honoured by every rental endpoint. Dates are inclusive calendar days in `timezone`
 * (default `UTC`). Company is honoured for `ALL_TENANTS` principals only; everybody else is pinned by
 * `resolveLocationScope` on the contract's owner location (`company_id` / `location_id`).
 * `locationIds` narrow the owner location; `stages` narrow the contract's current stage.
 * @interface
 */
export interface IRentalAnalyticsFilter {
  dateFrom: string;
  dateTo: string;
  timezone?: string;
  companyId?: number;
  locationIds?: number[];
  stages?: UsageStage[];
}

/** @interface */
export interface IRentalTrendParams extends IRentalAnalyticsFilter {
  bucket?: UsageTrendBucket;
}

/**
 * Estimate requests raised on contracts (`contract_event_logs.action = requestEstimate`) in the period.
 * @interface
 * @property {number} requests - Request events dated in the period.
 * @property {number} contracts - Distinct contracts those requests belong to.
 */
export interface IRentalEstimateCounts {
  requests: number;
  contracts: number;
}

/**
 * Child inspections of the scoped contracts by kind: `requested` = created in the period,
 * `completed` = `service_completed_at` in the period (stage completed).
 * @interface
 */
export interface IRentalInspectionKindCounts {
  kind: RentalInspectionKind;
  requested: number;
  completed: number;
}

/** Current contract status of the requested cohort, with its stage. */
export interface IRentalStatusBreakdownItem {
  status: StatusContractInspection;
  stage: UsageStage;
  contracts: number;
}

/**
 * Rental summary tiles. `requested` / `completed` are two independent series over the period
 * (contracts created in it / contracts first completed in it); `cohort` is the current stage of the
 * requested contracts and always reconciles: `requested = cohort.completed + cohort.inProgress + cohort.cancelled`.
 * @interface
 * @property {number} missedPickUp - Requested cohort currently in `MISSED_PICK_UP` (the rental attention tile).
 * @property {IUsageDurationStats} turnaround - `service_completed_at − created_at` over contracts completed in the period.
 */
export interface IRentalSummaryResult {
  period: IUsagePeriod;
  requested: number;
  completed: number;
  completionRate: number | null;
  cohort: IUsageCohortBreakdown;
  missedPickUp: number;
  estimates: IRentalEstimateCounts;
  inspections: IRentalInspectionKindCounts[];
  statuses: IRentalStatusBreakdownItem[];
  turnaround: IUsageDurationStats;
}

/** One trend point; `bucket` is the local (timezone) bucket start as `YYYY-MM-DD`. */
export interface IRentalTrendPoint {
  bucket: string;
  requested: number;
  completed: number;
  estimateRequests: number;
}

export interface IRentalTrendResult {
  period: IUsagePeriod;
  bucket: UsageTrendBucket;
  items: IRentalTrendPoint[];
}

/**
 * Per owner-location row (or per company when `groupBy = company`, then `locationId` is null).
 * `pickUps` / `dropOffs` count the contracts requested in the period whose pick-up / drop-off
 * location is this location, whoever owns them (under `company` grouping they equal `requested`).
 * @interface
 */
export interface IRentalByLocationItem {
  companyId: number;
  locationId: number | null;
  requested: number;
  completed: number;
  completionRate: number | null;
  inProgress: number;
  cancelled: number;
  pickUps: number;
  dropOffs: number;
  companyName?: string;
  locationName?: string;
}

export interface IRentalByLocationResult extends IPaginated<IRentalByLocationItem[]> {
  period: IUsagePeriod;
  groupBy: UsageLocationGroupBy;
}

/** @interface */
export interface IPaginationSearchRentalByLocation extends IPaginationSearch, IRentalAnalyticsFilter {
  sortBy?: UsageByLocationSortField;
  groupBy?: UsageLocationGroupBy;
}

/** One child inspection of a contract record. */
export interface IRentalContractInspectionView {
  inspectionId: string;
  kind: RentalInspectionKind;
  status: StatusInspection;
  stage: UsageStage;
  requestedAt: string;
  completedAt: string | null;
}

/**
 * One contract — the drill-down record and the export line — with its child inspections.
 * @interface
 * @property {number} estimateRequests - Lifetime `requestEstimate` events on the contract.
 */
export interface IRentalContractRecordView {
  contractId: string;
  contractNumber: string;
  vin: string | null;
  companyId: number;
  locationId: number;
  pickUpLocationId: number;
  dropOffLocationId: number;
  status: StatusContractInspection;
  stage: UsageStage;
  requestedAt: string;
  completedAt: string | null;
  pickUpDate: string;
  dropOffDate: string;
  estimateRequests: number;
  requestedInPeriod: boolean;
  completedInPeriod: boolean;
  inspections: IRentalContractInspectionView[];
  companyName?: string;
  locationName?: string;
}

/**
 * Record-level search (records and export only).
 * @interface
 * @property {string} [contractNumber] - Case-insensitive contains-match on `contract_number`.
 * @property {string} [vin] - Case-insensitive contains-match on `vin_number`.
 */
export interface IRentalRecordSearch {
  contractNumber?: string;
  vin?: string;
}

/** @interface */
export interface IPaginationSearchRentalRecords extends IPaginationSearch, IRentalAnalyticsFilter, IRentalRecordSearch {
  dateKind?: UsageDateKind;
  sortBy?: RentalRecordsSortField;
}

/** @interface */
export interface IExportRentalRecords extends IRentalAnalyticsFilter, IRentalRecordSearch {
  format: TableExportFormat;
  dateKind?: UsageDateKind;
}

export interface IExportedRentalRecordsFile extends IExportedFile {}

/** Export column keys (stable header order). */
export enum RentalExportColumn {
  CONTRACT_NUMBER = 'contractNumber',
  CONTRACT_ID = 'contractId',
  VIN = 'vin',
  DATE_REQUESTED = 'dateRequested',
  DATE_COMPLETED = 'dateCompleted',
  PICK_UP_DATE = 'pickUpDate',
  DROP_OFF_DATE = 'dropOffDate',
  STAGE = 'stage',
  CONTRACT_STATUS = 'contractStatus',
  ESTIMATE_REQUESTS = 'estimateRequests',
  PICK_UP_INSPECTION = 'pickUpInspection',
  DROP_OFF_INSPECTION = 'dropOffInspection',
  EMPLOYEE_INSPECTION = 'employeeInspection',
  COMPANY_ID = 'companyId',
  COMPANY_NAME = 'companyName',
  LOCATION_ID = 'locationId',
  LOCATION_NAME = 'locationName',
  PICK_UP_LOCATION_ID = 'pickUpLocationId',
  DROP_OFF_LOCATION_ID = 'dropOffLocationId',
  REQUESTED_IN_PERIOD = 'requestedInPeriod',
  COMPLETED_IN_PERIOD = 'completedInPeriod',
}

/**
 * One annotated definition per rental export column — see `USAGE_EXPORT_COLUMNS` for why the label
 * and the description live together rather than in two maps.
 */
export const RENTAL_EXPORT_COLUMNS: Readonly<Record<RentalExportColumn, IUsageExportColumnAnnotation>> = {
  [RentalExportColumn.CONTRACT_NUMBER]: { label: 'Contract #', description: 'The contract as people quote it.' },
  [RentalExportColumn.CONTRACT_ID]: { label: 'Contract ID', description: 'Its internal `con_…` id.' },
  [RentalExportColumn.VIN]: { label: 'VIN', description: 'The vehicle on the contract.' },
  [RentalExportColumn.DATE_REQUESTED]: { label: 'Date requested', description: 'When the contract was created.' },
  [RentalExportColumn.DATE_COMPLETED]: {
    label: 'Date completed',
    description: 'When it first reached completed; empty until it does.',
  },
  [RentalExportColumn.PICK_UP_DATE]: { label: 'Pick-up date', description: 'Scheduled collection.' },
  [RentalExportColumn.DROP_OFF_DATE]: { label: 'Drop-off date', description: 'Scheduled return.' },
  [RentalExportColumn.STAGE]: { label: 'Status', description: 'The contract phase, in five steps.' },
  [RentalExportColumn.CONTRACT_STATUS]: {
    label: 'Contract status',
    description: 'The raw status underneath that phase.',
  },
  [RentalExportColumn.ESTIMATE_REQUESTS]: {
    label: 'Estimate requests',
    description: 'How many estimates were raised on the contract.',
  },
  [RentalExportColumn.PICK_UP_INSPECTION]: {
    label: 'Pick-up inspection',
    description: 'The child inspection taken at collection, or empty if there was none.',
  },
  [RentalExportColumn.DROP_OFF_INSPECTION]: {
    label: 'Drop-off inspection',
    description: 'The one taken at return.',
  },
  [RentalExportColumn.EMPLOYEE_INSPECTION]: {
    label: 'Employee inspection',
    description: 'The one an employee ran, when the flow includes it.',
  },
  [RentalExportColumn.COMPANY_ID]: { label: 'Company ID', description: 'The owning company.' },
  [RentalExportColumn.COMPANY_NAME]: {
    label: 'Company',
    description: "The company's name, as company-service holds it.",
  },
  [RentalExportColumn.LOCATION_ID]: {
    label: 'Location ID',
    description: 'Where the contract belongs — the only location that carries a name here.',
  },
  [RentalExportColumn.LOCATION_NAME]: { label: 'Location', description: "The owning location's name." },
  [RentalExportColumn.PICK_UP_LOCATION_ID]: {
    label: 'Pick-up location ID',
    description: 'Where it was collected; a contract can be collected away from its own branch.',
  },
  [RentalExportColumn.DROP_OFF_LOCATION_ID]: {
    label: 'Drop-off location ID',
    description: 'Where it was returned.',
  },
  [RentalExportColumn.REQUESTED_IN_PERIOD]: {
    label: 'Requested in period',
    description: 'Y when the contract was created inside the selected dates.',
  },
  [RentalExportColumn.COMPLETED_IN_PERIOD]: {
    label: 'Completed in period',
    description: 'Y when it completed inside them. Both can be Y, or neither.',
  },
};

/** What each contract phase means. Rental names them for the vehicle, not the paperwork. */
export const RENTAL_STAGE_DESCRIPTIONS: Readonly<Record<UsageStage, string>> = {
  [UsageStage.AWAITING]: 'Booked, waiting for the vehicle to be collected.',
  [UsageStage.CAPTURE]: 'Out with the renter.',
  [UsageStage.REVIEW]: 'Back, with an estimate asked for.',
  [UsageStage.COMPLETED]: 'Closed out.',
  [UsageStage.CLOSED]: 'Cancelled or deleted before an outcome.',
};

/**
 * The rental summary tiles, keyed by the field on {@link IRentalSummaryResult} each one reads.
 *
 * Same period-versus-cohort split as {@link USAGE_SUMMARY_METRICS}, one unit throughout: the
 * contract. Its child inspections are counted on their own track and never here.
 */
export type RentalSummaryMetric =
  | 'requested'
  | 'completed'
  | 'completionRate'
  | 'cohort.inProgress'
  | 'cohort.cancelled'
  | 'missedPickUp'
  | 'estimates.requests'
  | 'turnaround';

export const RENTAL_SUMMARY_METRICS: Readonly<Record<RentalSummaryMetric, IAnalyticsMetricAnnotation>> = {
  completed: {
    label: 'Contracts completed',
    description:
      'Contracts whose service completed inside the selected period, whenever they were created — a contract opened last month and closed this week counts here.',
  },
  requested: {
    label: 'Contracts created',
    description:
      'Contracts created inside the period. The breakdown underneath follows that same cohort to wherever it stands now, so it keeps moving after the period has closed.',
  },
  completionRate: {
    label: 'Completion rate',
    description:
      'The two are not the same contracts — one created earlier can complete inside the period — so this reads as throughput rather than as a success rate.',
    formula: 'Contracts completed ÷ Contracts created',
  },
  'cohort.inProgress': {
    label: 'Contracts in progress',
    description:
      'Of the contracts created in this period, those in an open phase right now: awaiting the client, in capture, or in review. A live number over a fixed cohort.',
  },
  'cohort.cancelled': {
    label: 'Contracts cancelled',
    description: 'Of the contracts created in this period, those now cancelled.',
  },
  missedPickUp: {
    label: 'Missed pick-up',
    description:
      'Contracts created in this period that now sit at Missed pick-up: the vehicle was never collected. Rental raises this where an inspection would be flagged Requires attention.',
  },
  'estimates.requests': {
    label: 'Estimate requests',
    description:
      'Estimate requests raised on these contracts inside the period, read from the contract event log. A contract asked twice counts twice — the line underneath counts the contracts instead.',
  },
  turnaround: {
    label: 'Turnaround',
    description:
      'Taken over the contracts that completed inside the period. Open contracts are not in it. The median leads, with the average reported beside it: a few contracts left open for weeks pull an average past anything typical.',
    formula: 'Completed − Created, median',
    example: 'Created Sep 1, 09:00 → Completed Sep 4, 12:00 = 3d 3h',
  },
};
